/* ════════ v5.2: Stakeholder view orchestration (Matrix / Table / Cards) ════════
   The historical renderStakeholders()/renderMendelowGrid() pair is called from ~20
   sites; both now delegate to renderStakeholderView(), which refreshes the stat strip
   + role-type filter + datalists, shows the one active view, and renders it. The real
   renderers are _renderStakeholderCards() / _renderMatrixDots() / renderStakeholderTable(). */
function stkView() { return (appMeta.stkView === 'table' || appMeta.stkView === 'cards') ? appMeta.stkView : 'matrix'; }
function stkColourMode() { return appMeta.stkColourBy === 'strategy' ? 'strategy' : 'category'; }
function stkColour(s) {
  if (stkColourMode() === 'strategy') return `var(${stkStrategy(s).c})`;
  const cat = STK_CATEGORIES.includes(s.category) ? s.category : 'Customer';
  return `var(--cat-${cat.toLowerCase()})`;
}

/* public entry points — every legacy call site lands here */
function renderStakeholders() { renderStakeholderView(); }
function renderMendelowGrid() { renderStakeholderView(); }

function renderStakeholderView() {
  _closeStakeholderMenus();
  if (typeof populateRoleTypeFilter === 'function') populateRoleTypeFilter();
  if (typeof updateStakeholderDatalist === 'function') updateStakeholderDatalist();
  if (typeof updateOrganisationDatalist === 'function') updateOrganisationDatalist();
  renderStakeholderStats();
  applyStakeholderViewChrome();
  const v = stkView();
  if (v !== 'cards') _clearStakeholderCards();
  if (v === 'table') renderStakeholderTable();
  else if (v === 'cards') _renderStakeholderCards();
  else _renderMatrixDots();
}

/* show only the active view; hide the card-empties when they don't belong to it;
   sync the segmented controls' active state */
function applyStakeholderViewChrome() {
  const v = stkView();
  const sec  = document.getElementById('piSection'); if (sec)  sec.style.display  = (v === 'matrix') ? '' : 'none';
  const grid = document.getElementById('stkGrid');   if (grid) grid.style.display = (v === 'cards')  ? '' : 'none';
  const tbl  = document.getElementById('stkTable');  if (tbl)  tbl.style.display  = (v === 'table')  ? '' : 'none';
  if (v !== 'cards') {
    const e1 = document.getElementById('stkEmpty');         if (e1) e1.style.display = 'none';
    const e2 = document.getElementById('stkEmptyFiltered'); if (e2) e2.style.display = 'none';
  }
  document.querySelectorAll('#stkViewSeg button').forEach(b => _setSelected(b, b.dataset.view === v));
  const cb = stkColourMode();
  document.querySelectorAll('#stkColourSeg button').forEach(b => _setSelected(b, b.dataset.cb === cb));
}

function setStkView(v) {
  if (!['matrix', 'table', 'cards'].includes(v)) return;
  if (stkView() === 'cards' && v !== 'cards') {
    _settleStakeholderCards();
    _clearStakeholderCards();
  }
  appMeta.stkView = v; markDirty();
  renderStakeholderView();
}
function setStkColourBy(mode) {
  if (!['category', 'strategy'].includes(mode)) return;
  appMeta.stkColourBy = mode; markDirty();
  renderStakeholderView();
}

/* Stat strip — Total + the four Mendelow strategies, counted from the FILTERED list. */
function renderStakeholderStats() {
  const host = document.getElementById('stkStats'); if (!host) return;
  const list = getFilteredStakeholders();
  const c = { mc: 0, ks: 0, ki: 0, mo: 0 };
  list.forEach(s => { c[stkStrategy(s).key]++; });
  const cell = (k, n, label) => `<div class="stk-stat${k === 'total' ? ' stk-stat--total' : ''}"${k === 'total' ? '' : ` data-k="${k}"`}><span class="n">${n}</span><span class="l">${label}</span></div>`;
  host.innerHTML =
    cell('total', list.length, list.length === 1 ? 'stakeholder' : 'stakeholders') +
    cell('mc', c.mc, 'Manage closely') +
    cell('ks', c.ks, 'Keep satisfied') +
    cell('ki', c.ki, 'Keep informed') +
    cell('mo', c.mo, 'Monitor');
  /* v8.6.2: register-section count in the summary bar (unfiltered total, RAID-count idiom) */
  const regCount = document.getElementById('stkRegisterCount');
  if (regCount) regCount.textContent = STAKEHOLDERS.length ? String(STAKEHOLDERS.length) : '';
}

/* Dense Table view — avatar, name, role·org, category chip, influence + interest bars,
   strategy chip, remove. Row click → editor drawer (wired in the drawer phase). Rows are
   built with closures so the real (numeric or string) id is preserved into the handlers. */
function renderStakeholderTable() {
  _closeStakeholderMenus();
  if (stkView() !== 'cards') _clearStakeholderCards();
  const host = document.getElementById('stkTable'); if (!host) return;
  host.innerHTML = '';
  const list = getSortedStakeholders();
  if (!list.length) {
    host.innerHTML = `<div class="stk-tempty">${STAKEHOLDERS.length === 0 ? 'No stakeholders yet — click “Add stakeholder” to begin.' : 'No stakeholders match your search.'}</div>`;
    return;
  }
  const head = document.createElement('div');
  head.className = 'stk-thead stk-tgrid';
  head.innerHTML = '<span></span><span>Name</span><span>Role · Org · Location</span><span>Category</span><span>Influence</span><span>Interest</span><span>Strategy</span><span></span>';
  host.appendChild(head);
  list.forEach(s => {
    const cat = STK_CATEGORIES.includes(s.category) ? s.category : 'Customer';
    const catv = cat.toLowerCase();
    const st = stkStrategy(s);
    const p = stkPI(s, 'power'), n = stkPI(s, 'interest');
    /* v14.1 (c): one more item in the array, exactly as the scope said for THIS surface.
       filter(Boolean) already drops blanks, so anyone without a location is unchanged. */
    const role = [s.role, s.organisation, s.location].filter(Boolean).join(' · ') || '—';
    /* v12 (W1): the chip IS the resource marker — no chip, stakeholder only */
    const allocChip = (typeof s.allocationPct === 'number')
      ? ` <span class="stk-alloc-chip" title="Allocated ${s.allocationPct}% of their time to this project">${s.allocationPct}%</span>`
      : '';
    const colour = stkColour(s);
    const bar = (val) => `<div class="stk-tbar-wrap stk-tbar-i"><div class="stk-tbar"><i style="width:${val}%;background:${colour}"></i></div><span class="stk-tbar-lvl">${stkLevel(val)}</span></div>`;
    const row = document.createElement('div');
    row.className = 'stk-trow stk-tgrid';
    row.dataset.id = s.id;
    row.innerHTML =
      `<div class="stk-tavatar" style="background:${colour}">${esc(stkInitials(s.name))}</div>` +
      `<div class="stk-tname" title="${esc(s.name || '')}">${esc(s.name || '(unnamed)')}</div>` +
      `<div class="stk-trole" title="${esc(role)}">${esc(role)}${allocChip}</div>` +
      `<div class="stk-tcat" style="color:var(--cat-${catv});background:var(--cat-${catv}-bg);border-color:var(--cat-${catv}-bd)">${cat}</div>` +
      bar(p) + bar(n) +
      `<div class="stk-tstrat" style="color:var(${st.c});background:var(${st.bg});border-color:var(${st.bd})">${esc(st.label)}</div>` +
      `<button class="stk-trm" title="Remove" type="button">×</button>`;
    row.onclick = () => { if (typeof openStakeholderEditor === 'function') openStakeholderEditor(s.id, 'table'); };
    const rm = row.querySelector('.stk-trm');
    if (rm) rm.onclick = (e) => { e.stopPropagation(); (typeof confirmDeleteStakeholder === 'function' ? confirmDeleteStakeholder : deleteStakeholder)(s.id); };
    host.appendChild(row);
  });
}

/* ════════ v5.2: stakeholder editor drawer (the shared edit form, 2nd frame) ════════
   Opens from a matrix-dot click and a table-row click (stakeholders only); cards edit
   inline (§7a). "+ Add" opens a blank drawer. Edits are live on the bound object; a
   blank just-added draft is discarded on Cancel or on close-while-empty (no orphan). */
let _stkDrawerCtx = null;
/* v17.0 W2: backdrop dismissal is a GESTURE, not merely the final click target.
   A right-to-left text-selection drag can begin in a drawer input and finish over the
   overlay, where the browser may deliver the final click. Remember the primary press
   origin: only a press that itself began on the backdrop is allowed to dismiss.
   This changes no close semantics — backdrop and X still discard a new draft; Cancel,
   Done/Add stakeholder and Delete continue through their existing handlers. */
let _stkDrawerBackdropPressed = false;
function _stkDrawerBackdropPointerDown(e) {
  _stkDrawerBackdropPressed = !!(e && e.isPrimary !== false && e.button === 0
    && e.target === e.currentTarget);
}
function _stkDrawerBackdropPointerCancel() {
  _stkDrawerBackdropPressed = false;
}
function _stkDrawerBackdropClick(e) {
  const dismiss = !!(e && e.target === e.currentTarget && _stkDrawerBackdropPressed);
  _stkDrawerBackdropPressed = false;
  if (dismiss) closeStakeholderDrawer(true);
}

function openStakeholderEditor(id, source) {
  const overlay = document.getElementById('stkDrawerOverlay'); if (!overlay) return;
  let s, isNew = false;
  if (id == null) {
    isNew = true;
    /* v13.1 (A40): no `roleTypes: []`. The stated invariant is that an EMPTY role-type list
       is the absence of the key, not a stored empty array — every reader already treats a
       missing roleTypes as none. Both creation paths wrote it anyway, putting 14 empty
       arrays on disk. Existing ones are dropped lazily on project activation. */
    s = { id: _nextStkSeq(), name: '', role: '', organisation: '', category: STK_NEW_DEFAULT_CATEGORY, power: 50, interest: 50, engagementCurrent: 'Neutral', engagementTarget: 'Supportive', notes: '' };
    STAKEHOLDERS.push(s);
  } else {
    s = STAKEHOLDERS.find(x => String(x.id) === String(id));
    if (!s) return;
  }
  _stkDrawerCtx = { id: s.id, isNew };
  _stkDrawerBackdropPressed = false;
  _buildStakeholderDrawer(s, isNew);
  renderStakeholderView();                 // reflect a fresh draft behind the drawer
  overlay.classList.add('open');
  const dr = document.getElementById('stkDrawer'); if (dr) dr.classList.add('open');
}

function closeStakeholderDrawer(discard) {
  _closeStakeholderMenus();
  _stkDrawerBackdropPressed = false;
  const drawer = document.getElementById('stkDrawer');
  const overlay = document.getElementById('stkDrawerOverlay');
  if (drawer) drawer.classList.remove('open');
  if (overlay) overlay.classList.remove('open');
  const ctx = _stkDrawerCtx; _stkDrawerCtx = null;
  if (ctx) {
    const s = STAKEHOLDERS.find(x => x.id === ctx.id);
    if (s && ctx.isNew && (discard || _stkIsBlank(s))) {
      /* v14.1 (f): say so when there was something to lose. Silent for the ordinary case -
         opened by mistake, typed nothing - because a toast for that is noise. */
      const hadContent = !_stkIsBlank(s);
      const i = STAKEHOLDERS.indexOf(s); if (i !== -1) STAKEHOLDERS.splice(i, 1);
      if (hadContent && typeof showToast === 'function') showToast('New stakeholder discarded.');
    } else if (s && ctx.isNew) {
      markDirty();                          // a real new stakeholder was committed
    }
  }
  renderStakeholderView();
}

/* "The user opened the drawer and typed nothing" — the test that decides whether a NEW
   stakeholder is kept or silently thrown away on "Add stakeholder".
   ⚠ v13.1 (A62): it has to name EVERY field the drawer can set, or the drawer offers an
   edit it then discards. Allocation and Category were missing: someone who entered a 40%
   resource, or set the category and nothing else, pressed "Add stakeholder" and watched the
   row never appear. Both are now counted — Category against the seed default ('Customer'
   in openStakeholderEditor), since a pick that equals the default is indistinguishable from
   never having touched the control. Adding a field to that drawer means adding it here. */
function _stkIsBlank(s) {
  /* ⚠ v14.1: `location` joins the list (c) and the category baseline now comes from
     STK_NEW_DEFAULT_CATEGORY (d). A62's rule stands: this must name EVERY field the drawer
     can set, or the drawer offers an edit it then silently discards. */
  return !(s.name || '').trim() && !(s.role || '').trim() && !(s.organisation || '').trim() && !(s.notes || '').trim()
    && !(s.location || '').trim()
    && (!Array.isArray(s.roleTypes) || s.roleTypes.length === 0)
    && typeof s.allocationPct !== 'number'
    && (s.category === undefined || s.category === STK_NEW_DEFAULT_CATEGORY)
    && stkPI(s, 'power') === 50 && stkPI(s, 'interest') === 50;
}

/* re-render the stat strip + the active underlying view (drawer sits on top) */
function _drawerRefresh() {
  renderStakeholderStats();
  const v = stkView();
  if (v === 'matrix') _renderMatrixDots();
  else if (v === 'table') renderStakeholderTable();
  else _renderStakeholderCards();
}

function _buildStakeholderDrawer(s, isNew) {
  _closeStakeholderMenus();
  const body = document.getElementById('stkDrawerBody');
  const foot = document.getElementById('stkDrawerFoot');
  const title = document.getElementById('stkDrawerTitle');
  if (!body || !foot) return;
  title.textContent = isNew ? 'New stakeholder' : (s.name || 'Stakeholder');
  body.innerHTML = '';
  const field = (text, control) => {
    const f = document.createElement('div'); f.className = 'stk-dfield';
    const l = document.createElement('label'); l.className = 'stk-dlabel'; l.textContent = text;
    f.append(l, control); return f;
  };

  const name = document.createElement('input'); name.type = 'text'; name.className = 'stk-dinput'; name.value = s.name || ''; name.placeholder = 'Full name';
  name.oninput = () => { title.textContent = name.value.trim() || (isNew ? 'New stakeholder' : 'Stakeholder'); };
  name.onblur = () => { s.name = name.value.trim(); markDirty(); _drawerRefresh(); };
  body.appendChild(field('Name', name));

  const role = document.createElement('input'); role.type = 'text'; role.className = 'stk-dinput'; role.value = s.role || ''; role.placeholder = 'Role / title';
  role.onblur = () => { s.role = role.value.trim(); markDirty(); _drawerRefresh(); };
  body.appendChild(field('Role / title', role));

  const comp = document.createElement('input'); comp.type = 'text'; comp.className = 'stk-dinput'; comp.setAttribute('list', 'organisationList'); comp.value = s.organisation || ''; comp.placeholder = 'Organisation';
  comp.onblur = () => { s.organisation = comp.value.trim(); markDirty(); _drawerRefresh(); };
  body.appendChild(field('Organisation', comp));

  /* v14.1 (c): one text input copying Organisation's own pattern. Placed directly after it,
     before Allocation, so the three 'who and where they are' facts sit together and the
     project-specific ones follow. Placeholder settled at the mockup: the short form.
     ⚠ Blank stores nothing - the handler deletes the key. */
  const dloc = document.createElement('input'); dloc.type = 'text'; dloc.className = 'stk-dinput';
  dloc.value = s.location || ''; dloc.placeholder = 'e.g. Munich';
  dloc.onblur = () => {
    const v = dloc.value.trim();
    if (v) s.location = v; else delete s.location;
    markDirty(); _drawerRefresh();
  };
  body.appendChild(field('Location', dloc));
  /* v12 (W1): the resource lens — the % field IS the flag. Clearing it DELETES the key
     (the WBS planned-dates precedent), so a stakeholder who was never a resource
     round-trips byte-identically. Typed, not slid: a % someone told you is a number;
     power/interest stay sliders because they are judgement positions. */
  const alloc = document.createElement('input'); alloc.type = 'number'; alloc.className = 'stk-dinput';
  alloc.min = 0; alloc.max = 100; alloc.step = 5; alloc.placeholder = 'e.g. 40 — empty = not a resource';
  alloc.value = (typeof s.allocationPct === 'number') ? s.allocationPct : '';
  alloc.onblur = () => {
    const v = parseInt(alloc.value, 10);
    if (!Number.isFinite(v) || v <= 0) { delete s.allocationPct; alloc.value = ''; }
    else { s.allocationPct = Math.min(100, v); alloc.value = s.allocationPct; }
    markDirty(); _drawerRefresh();
  };
  body.appendChild(field('Allocation — % of their time on this project', alloc));

  const cat = document.createElement('select'); cat.className = 'stk-dinput';
  const curCat = STK_CATEGORIES.includes(s.category) ? s.category : 'Customer';
  STK_CATEGORIES.forEach(c => { const o = document.createElement('option'); o.value = c; o.textContent = c; if (c === curCat) o.selected = true; cat.appendChild(o); });
  cat.onchange = () => { s.category = cat.value; markDirty(); _buildStakeholderDrawer(s, isNew); _drawerRefresh(); };
  body.appendChild(field('Category', cat));

  body.appendChild(buildRoleTypeRow(s));                                       // own "Role type" label
  body.appendChild(field('Influence (power)', buildPISlider(s, 'power', _drawerRefresh, true)));
  body.appendChild(field('Interest', buildPISlider(s, 'interest', _drawerRefresh, true)));

  const notes = document.createElement('textarea'); notes.className = 'stk-dinput stk-dnotes'; notes.value = s.notes || ''; notes.placeholder = 'Notes…';
  notes.onblur = () => { s.notes = notes.value; markDirty(); _drawerRefresh(); };
  body.appendChild(field('Notes', notes));

  foot.innerHTML = '';
  const left = document.createElement('button'); left.type = 'button'; left.className = 'stk-dbtn stk-dbtn--danger';
  if (isNew) { left.textContent = 'Cancel'; left.onclick = () => closeStakeholderDrawer(true); }
  else { left.textContent = 'Delete'; left.onclick = () => { const did = s.id; closeStakeholderDrawer(); if (typeof confirmDeleteStakeholder === 'function') confirmDeleteStakeholder(did); }; }
  const done = document.createElement('button'); done.type = 'button'; done.className = 'stk-dbtn stk-dbtn--primary'; done.textContent = isNew ? 'Add stakeholder' : 'Done';
  done.onclick = () => closeStakeholderDrawer();
  foot.append(left, done);
}

/* ══ v12 (W2): "Add from portfolio…" — copy people across, never re-type them ═══════
   The union is COMPUTED from every project in the file (possible only because v11 put
   the whole portfolio in ONE file) and stored NOWHERE. Copies, not links — his call:
   name/organisation/category are copied; role and engagement stay blank (per-project
   facts); ids are fresh stkSeq mints, so RACI and comms cannot be disturbed. */
function _ppKey(name, org) { return ((name || '').trim() + '|' + (org || '').trim()).toLowerCase(); }
function _portfolioPeopleUnion() {
  const out = new Map();
  (((window.__DB__ || {}).projects) || []).forEach(p => {
    (Array.isArray(p && p.stakeholders) ? p.stakeholders : []).forEach(s => {
      const nm = (s && typeof s.name === 'string') ? s.name.trim() : '';
      if (!nm) return;
      const org = (s && typeof s.organisation === 'string') ? s.organisation.trim() : '';
      const key = _ppKey(nm, org);
      let e = out.get(key);
      if (!e) {
        e = { name: nm, organisation: org,
              category: (STK_CATEGORIES.includes(s.category) ? s.category : 'Customer'),
              sources: [] };
        out.set(key, e);
      }
      /* v12.3 (W1): an ARCHIVED source project is labelled where it is named. `sources` is a
         DISPLAY-only list - it is written here and read in exactly one place (_renderPpList's
         "in: ..." line); addPortfolioPeople copies name/organisation/category and never touches
         it - so carrying the label in the string costs nothing and changes no data. The dedup
         key (_ppKey, name|organisation) is untouched, so nobody splits in two.
         Deliberately NOT a filter: someone who exists only in archived projects stays offerable,
         just visibly labelled - his call ("label them as archived"). */
      const pn = (p && p.name) ? (p.archived ? p.name + ' (archived)' : p.name) : '';
      if (pn && e.sources.indexOf(pn) === -1) e.sources.push(pn);
    });
  });
  return out;
}
function openPortfolioPeoplePicker() {
  const search = document.getElementById('ppPickerSearch');
  if (search) search.value = '';
  _renderPpList('');
  const ov = document.getElementById('ppPickerOverlay');
  if (ov) ov.classList.add('open');
  if (search) search.focus();
}
function closePortfolioPeoplePicker() {
  const ov = document.getElementById('ppPickerOverlay');
  if (ov) ov.classList.remove('open');
}
function _renderPpList(filter) {
  const host = document.getElementById('ppPickerList');
  if (!host) return;
  const here = new Set(STAKEHOLDERS.map(s => _ppKey(s.name, s.organisation)));
  const q = (filter || '').trim().toLowerCase();
  const rows = [];
  _portfolioPeopleUnion().forEach((e, key) => {
    if (q && (e.name + ' ' + e.organisation).toLowerCase().indexOf(q) === -1) return;
    rows.push({ key: key, e: e, grey: here.has(key) });
  });
  rows.sort((a, b) => a.e.name.localeCompare(b.e.name));
  host.innerHTML = rows.length ? '' : '<div class="pp-none">Nobody found — people appear here once they exist in any project in this portfolio.</div>';
  rows.forEach(r => {
    const row = document.createElement('label');
    row.className = 'pp-row' + (r.grey ? ' pp-grey' : '');
    const cb = document.createElement('input'); cb.type = 'checkbox';
    cb.dataset.key = r.key; cb.disabled = r.grey;
    cb.onchange = _ppUpdateCount;
    const who = document.createElement('div');
    who.innerHTML = `<div class="pp-nm">${esc(r.e.name)}</div>`
      + (r.e.organisation ? `<div class="pp-org">${esc(r.e.organisation)}</div>` : '')
      /* v12.3 (W1): the sources already carry "(archived)" where it applies (see
         _portfolioPeopleUnion) - nothing to decide here, which is the point of putting the label
         at the single place the fact is known. */
      + `<div class="pp-in">${r.grey ? 'already in this project' : 'in: ' + esc(r.e.sources.join(' · '))}</div>`;
    row.append(cb, who);
    host.appendChild(row);
  });
  _ppUpdateCount();
}
function _ppUpdateCount() {
  const n = document.querySelectorAll('#ppPickerList input:checked').length;
  const btn = document.getElementById('ppAddBtn');
  if (btn) { btn.textContent = 'Add ' + n + (n === 1 ? ' person' : ' people'); btn.disabled = !n; }
}
function addPortfolioPeople() {
  const union = _portfolioPeopleUnion();
  const picked = [...document.querySelectorAll('#ppPickerList input:checked')]
    .map(cb => union.get(cb.dataset.key)).filter(Boolean);
  if (!picked.length) return;
  picked.forEach(e => {
    STAKEHOLDERS.push({ id: _nextStkSeq(), name: e.name, role: '', organisation: e.organisation,
      /* v13.1 (A40): the second creation path — see openStakeholderEditor. */
      category: e.category, power: 50, interest: 50,
      engagementCurrent: 'Neutral', engagementTarget: 'Supportive', notes: '' });
  });
  markDirty();
  closePortfolioPeoplePicker();
  if (typeof updateOrganisationDatalist === 'function') updateOrganisationDatalist();
  renderStakeholders();
  if (typeof renderStakeholderStats === 'function') renderStakeholderStats();
  if (typeof renderMendelowGrid === 'function') renderMendelowGrid();
  if (typeof showToast === 'function') showToast('Added ' + picked.length + (picked.length === 1 ? ' person' : ' people') + ' from the portfolio', { type: 'info' });
}

/* ── Mendelow Power/Interest grid auto-plot ─────────────────────────── */

/* v3.4: which categories are currently shown on the grid. ALL shown by default.
   This is transient UI state (resets to all-on on reload) — the chips are a quick
   "drop a whole class off the grid" affordance, not a persisted preference. */
const activeGridCategories = new Set(STK_CATEGORIES);

/* v10.3 (W5): applyGridCategoryClasses removed with its only caller
   (toggleDotColorByCategory, below) — the "Colour dots by category" checkbox left the
   markup long ago, so #dotColorChk was a permanently-null lookup and the
   grid-color-by-cat class was never applied. Dot colouring is done in JS by
   stkColour(); the CSS rules gated on that class targeted the dead .pi-dot,
   whose own rules v13.1 (U04) finally removed. */
/* Filter-chip click — toggle a whole category on/off the grid. */
function toggleGridCategory(cat) {
  if (!STK_CATEGORIES.includes(cat)) return;
  if (activeGridCategories.has(cat)) activeGridCategories.delete(cat);
  else activeGridCategories.add(cat);
  const chip = document.querySelector('.filter-chip[data-cat="' + cat + '"]');
  /* v13.1 (A34/A58): 'off' is the chip's own inverted class, so the pressed state is
     written from the same one truth rather than tracked a second time. */
  if (chip) { const on = activeGridCategories.has(cat);
              chip.classList.toggle('off', !on);
              chip.setAttribute('aria-pressed', on ? 'true' : 'false'); }
  /* v4.0: tab-wide — re-render cards AND grid from the shared filtered list
     (mirrors the search-filter refresh pattern). */
  renderStakeholders();
  renderMendelowGrid();
}

function _renderMatrixDots() {
  _closeStakeholderMenus();
  if (stkView() !== 'cards') _clearStakeholderCards();
  const layer = document.getElementById('piDots');
  if (!layer) return;
  layer.innerHTML = '';
  /* consume the FILTERED list so the grid mirrors search/category/role filters */
  const visible = getFilteredStakeholders();
  visible.forEach(s => {
    const x = stkPI(s, 'interest');   // 0–100 → left
    const y = stkPI(s, 'power');      // 0–100 → bottom (higher power = higher up)
    const colour = stkColour(s);      // v5.2: by category or strategy (toolbar toggle)

    const pt = document.createElement('div');
    pt.className = 'pi-pt';
    pt.style.left = x + '%';
    pt.style.bottom = y + '%';
    pt.dataset.id = s.id;
    /* ── v13.1 (A35): flip the hover label below the dot near the top of the grid ────────
       .pi-wrap is overflow:hidden (it has to be — the rounded corners clip the quadrant
       fills), so a label drawn ABOVE a dot high on the Influence axis is simply cut off,
       and the stakeholders it hides are the most important ones on the board.
       The threshold is arithmetic, not taste: the label sits 6px + its own ~22px above the
       dot's top, and the dot's top is (y% of the 345px plot area) + 15px up from the
       bottom, so it leaves the box above y ≈ 87.5. Measured on the shipped grid: clipped at
       88 (1px), 90 (8px), 95 (25px) and 98 (35px); clear at 85 and below. 85 is the flip
       point, keeping a small margin. Deliberately computed from y rather than measured
       from the DOM: this render also runs while the People panel is display:none, where
       every rectangle is 0 and a measurement would silently decide "not clipped". */
    if (y > 85) pt.classList.add('pi-below');

    const name = document.createElement('div');
    name.className = 'pi-cname';
    name.textContent = (s.name || '(unnamed)') + (s.role ? ' · ' + s.role : '');

    const dot = document.createElement('div');
    dot.className = 'pi-cdot';
    dot.style.background = colour;
    dot.textContent = stkInitials(s.name);
    dot.title = s.name || '(unnamed)';
    dot.dataset.id = s.id;
    dot.addEventListener('mouseenter', _piFocusOn);
    dot.addEventListener('mouseleave', _piFocusOff);
    dot.addEventListener('click', () => { if (typeof openStakeholderEditor === 'function') openStakeholderEditor(s.id, 'matrix'); });

    pt.appendChild(name);
    pt.appendChild(dot);
    layer.appendChild(pt);
  });

  /* v16 (a): nothing above this line changed — every dot is still written at its TRUE
     position first, and the pass below only moves the ones that would cover each other. */
  _piSpreadOverlaps(layer);
}

/* ── v16 (a): separate stakeholder dots that would cover one another ──────────────────
   Two stakeholders with the same — or merely NEARBY — power and interest were drawn on top
   of each other: the initials became unreadable and the dot underneath could not be reached.
   ⚠ The report said "same power/interest values", and the real trigger is wider. Measured on
   a copy of the production portfolio, that file contains ZERO exact ties and three pairs
   still overlapped, the worst at 54%: a 30px dot spans roughly 8 points of POWER on a 380px
   plot, so anyone within ~8 points of a neighbour collides.
   ⚠ THIS IS A RESTORED CAPABILITY, NOT A NEW ONE. Collision offsets existed as
   `collisionOffsets` in the v3.0/v5.1.1 dot machinery; v5.2 replaced that machinery with this
   overlay renderer and never reimplemented them, and v10.3 (W5) then swept the dead code. So
   the matrix has drawn overlapping dots since v5.2 — which answers "did this ever work": it
   did, before v5.2.
   ⚠ DETERMINISTIC, NEVER JITTERED. A random nudge repaints differently every time, cannot be
   asserted, and makes the board twitch. Dots are placed in a stable id order: the first of a
   cluster KEEPS ITS TRUE POSITION and later ones take the first free slot on a fixed spiral,
   so a dot that collides with nothing never moves at all.
   ⚠ SIZES ARE MEASURED FROM THE RENDERED LAYER, NEVER ASSUMED (§v15.3 (d)'s law) — and if the
   People pane is hidden the layer measures 0×0, in which case this returns and leaves every
   dot at its true position rather than computing nonsense from a zero. That zero is this
   repo's oldest trap and it is a guard here, not an accident. */
function _piSpreadOverlaps(layer) {
  const pts = Array.from(layer.querySelectorAll('.pi-pt'));
  if (pts.length < 2) return;

  const lr = layer.getBoundingClientRect();
  const firstDot = pts[0].querySelector('.pi-cdot');
  const D = firstDot ? firstDot.getBoundingClientRect().width : 0;
  if (lr.width < 1 || lr.height < 1 || D < 1) return;   /* pane hidden — leave true positions */

  /* Work in pixels, y measured UP from the bottom, matching the `bottom:%` the render wrote. */
  const items = pts.map(pt => ({
    pt: pt,
    id: String(pt.dataset.id),
    x: (parseFloat(pt.style.left) / 100) * lr.width,
    y: (parseFloat(pt.style.bottom) / 100) * lr.height
  })).sort((a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true }));

  const placed = [];
  const withheld = [];          /* v16.1 (b): dots the rings could not fit */
  const isClear = (px, py) => placed.every(q => Math.hypot(px - q.px, py - q.py) >= D);
  const TURNS = 8;              /* candidate angles per ring */
  const STEP  = D * 0.85;       /* radial step — just over one dot, so rings stay compact */

  items.forEach(it => {
    let px = it.x, py = it.y;
    if (!isClear(px, py)) {
      let found = false;
      for (let ring = 1; ring <= 6 && !found; ring++) {
        for (let k = 0; k < TURNS && !found; k++) {
          const a = (2 * Math.PI * k) / TURNS;
          const cx = it.x + Math.cos(a) * STEP * ring;
          const cy = it.y + Math.sin(a) * STEP * ring;
          if (isClear(cx, cy)) { px = cx; py = cy; found = true; }
        }
      }
      /* ── v16.1 (b): EXHAUSTED. v16 left the dot at its true position here — safe, but it
         silently went back to covering somebody, and no check could tell that apart from a
         bug. Now the dot is WITHHELD and rolled up into a `+N more` badge below (his ruling:
         "+6 more sounds good as an example").
         ⚠ `return` BEFORE placed.push, deliberately: a withheld dot is not on the board, so
         it must not occupy a slot that a later dot could use. */
      if (!found) { withheld.push(it); return; }
    }
    placed.push({ px: px, py: py });

    const dx = px - it.x, dy = py - it.y;
    if (dx || dy) {
      /* .pi-pt already carries translate(-50%, 50%) — APPEND to it, never replace it, or the
         dot stops being centred on its own coordinate. CSS y grows DOWNWARD, ours grows up. */
      it.pt.style.transform =
        'translate(-50%, 50%) translate(' + dx.toFixed(2) + 'px, ' + (-dy).toFixed(2) + 'px)';
      it.pt.dataset.nudged = '1';   /* data-, not a class: nothing in CSS keys off it */
      /* v13.1 (A35)'s label flip is decided from the dot's height in the plot, so recompute it
         from where the dot ENDED UP — a nudge upward can push a label into the clipped zone. */
      it.pt.classList.toggle('pi-below', (py / lr.height) * 100 > 85);
    }
  });

  /* v16.1 (b): nothing at all happens below the exhaustion threshold — at his real density
     (10 stakeholders) and the fixture's, `withheld` is empty and this is a no-op. */
  if (withheld.length) _piRollUpWithheld(layer, withheld, lr, D, placed);
}

/* ── v16.1 (b): roll withheld dots up into one `+N more` badge per crowded cluster ──────
   WHY A BADGE AND NOT A BIGGER RING BUDGET. Widening the ring count moves the cliff instead
   of removing it: at some density every layout runs out of room, and the old failure was
   invisible rather than wrong. MEASURED on the real algorithm before choosing: ring 1 is
   unusable for an exact tie (radius 0.85·D < D, so every ring-1 candidate still collides
   with the centre dot), radially adjacent rings block each other at equal angles, and the
   first density that cannot be fully placed is THIRTY co-located dots — far below the
   nominal 6×8 = 48 slots. A crowded cluster spreads up to ±40 interest and ±44 power points.
   ⚠ ONE BADGE PER CLUSTER, never one per withheld dot — a pile of badges in a full corner
   would reproduce the very problem this exists to solve.
   ⚠ DETERMINISTIC, like the offset it completes: `items` is sorted by id upstream, so
   `withheld` arrives in id order, so grouping and centroids are stable across renders.
   ⚠ NOTHING VANISHES WITHOUT TRACE: the badge's title names everybody it stands for, and the
   register list and table were never filtered by this — they always showed everyone. */
function _piRollUpWithheld(layer, withheld, lr, D, placed) {
  const groups = [];
  withheld.forEach(it => {
    /* group on TRUE positions: these dots never got a placed position to group on */
    const g = groups.find(q => Math.hypot(it.x - q.ax, it.y - q.ay) < D * 2);
    if (g) g.members.push(it);
    else groups.push({ ax: it.x, ay: it.y, members: [it] });
  });

  groups.forEach(g => {
    const n = g.members.length;
    const names = g.members.map(it => {
      const d = it.pt.querySelector('.pi-cdot');
      return (d && d.title) || '(unnamed)';
    }).sort((a, b) => a.localeCompare(b));

    /* take them off the board — they were only ever drawn to be measured */
    g.members.forEach(it => { if (it.pt.parentNode) it.pt.parentNode.removeChild(it.pt); });

    const cx = g.members.reduce((s, m) => s + m.x, 0) / n;
    const cy = g.members.reduce((s, m) => s + m.y, 0) / n;

    const badge = document.createElement('div');
    badge.className = 'pi-more';
    badge.textContent = '+' + n + ' more';
    badge.title = n + ' more here, no room to show them separately: ' + names.join(', ');
    badge.dataset.count = String(n);

    /* ⚠⚠ THE BADGE MUST NOT COVER A DOT, and this was found by LOOKING AT THE BOARD rather
       than by any check. Drawn at the centroid it landed exactly on the first dot of the
       cluster — the one that by design keeps its TRUE position — so the thing announcing
       'people are hidden here' was itself hiding a person. That is the very defect v16
       existed to fix, in miniature.
       So the badge takes the first CLEAR slot on the same deterministic spiral the dots use,
       starting from the centroid. Overlap is tested as rectangle-vs-square (the badge is wide
       and short; a dot is D across), never as centre distance, which would under-report for a
       62px-wide label. If nothing is clear it stays at the centroid — degenerate, but the
       count is still legible above the dots because .pi-more carries a higher z-index. */
    layer.appendChild(badge);                       /* appended first, so it can be measured */
    const br = badge.getBoundingClientRect();
    const hw = (br.width || 62) / 2, hh = (br.height || 22) / 2;
    const clearOfDots = (px, py) => (placed || []).every(q =>
      Math.abs(px - q.px) >= hw + D / 2 || Math.abs(py - q.py) >= hh + D / 2);

    /* ⚠⚠ THE BADGE GOES OUTSIDE THE CLUSTER, NOT INSIDE IT — and that was MEASURED, after a
       first attempt that hunted for a gap using the dots' own spiral and never found one.
       A cluster dense enough to exhaust the rings is a ~150px blob packed with dots, and the
       ring step (0.85·D ≈ 25.5px) is half a pixel under the 26px a 22px-tall badge needs to
       clear a 30px dot — so every candidate failed and the badge stayed at the centroid,
       covering the cluster's first dot completely. There is no gap to find; stop looking for
       one. It sits just past the cluster's own outer edge instead.
       ⚠ DETERMINISTIC: a FIXED direction order, up first (a label above its cluster reads
       naturally), and the first direction that both stays inside the plot and clears every
       dot wins. Same portfolio, same badge, same place, every render. */
    let bx = cx, by = cy;
    if (!clearOfDots(bx, by)) {
      /* the cluster's own radius: only dots near this centroid count, so two separate
         crowded corners do not inflate each other's badge distance */
      let outerR = 0;
      (placed || []).forEach(q => {
        const d2 = Math.hypot(q.px - cx, q.py - cy);
        if (d2 <= D * 8 && d2 > outerR) outerR = d2;
      });
      /* ⚠ max(hw, hh), NOT hh: a horizontal direction needs the badge's HALF-WIDTH of
         clearance (31px here), not its half-height (11px). Using hh put every candidate
         ~30px from a ring-6 dot, which fails the 46px horizontal test, so nothing was ever
         clear and the badge stayed on top of the dot it was meant to stand beside. */
      const R = outerR + Math.max(hw, hh) + D / 2 + 4;
      /* ⚠ HORIZONTAL FIRST, and that order came from looking at the board too. Straight up
         put the badge over the quadrant caption ("High power, high interest"), because the
         captions live at the TOP of each quadrant. Going out sideways keeps the badge at its
         own cluster's height — which also reads more clearly as belonging to it — and clear
         of the captions. Covering static furniture was never as bad as covering a person,
         but neither is necessary. */
      const DIRS = [0, -45, 45, 180, -135, 135, -90, 90];   /* out sideways, then diagonals */
      let found = false;
      for (let i = 0; i < DIRS.length && !found; i++) {
        const a = DIRS[i] * Math.PI / 180;
        const tx = cx + Math.cos(a) * R, ty = cy + Math.sin(a) * R;
        if (tx - hw < 0 || tx + hw > lr.width || ty - hh < 0 || ty + hh > lr.height) continue;
        if (clearOfDots(tx, ty)) { bx = tx; by = ty; found = true; }
      }
      /* nothing clear anywhere: stay at the centroid. The count is still legible above the
         dots (.pi-more carries the higher z-index) — degenerate, not broken. */
    }
    badge.style.left = ((bx / lr.width) * 100).toFixed(3) + '%';
    badge.style.bottom = ((by / lr.height) * 100).toFixed(3) + '%';
  });
}

/* v5.2: hover-focus on the continuous matrix — dim the other names, lift the hovered
   dot + its name. Matches the v5.1.1 behaviour, on the new overlay. */
function _piFocusOn(e) {
  const id = e.currentTarget.dataset.id;
  const layer = document.getElementById('piDots'); if (!layer) return;
  layer.classList.add('focusing');
  const pt = e.currentTarget.closest('.pi-pt'); if (pt) pt.classList.add('focused');
}
function _piFocusOff() {
  const layer = document.getElementById('piDots'); if (!layer) return;
  layer.classList.remove('focusing');
  layer.querySelectorAll('.pi-pt.focused').forEach(el => el.classList.remove('focused'));
}

/* initials for a dot: first letters of the first two words, uppercase */
function stkInitials(name) {
  return (name || '').trim().split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';
}

/* v10.3 (W5): the retired v3.0/v5.1.1 Mendelow dot-placement machinery is gone —
   collisionOffsets, computeStakeholderPosition, placeDotInDOM, _stkDotFocusOn/Off,
   positionForLevel and abbreviateName. Superseded by _renderMatrixDots (the overlay
   dots + .pi-pt/.pi-cdot/.pi-cname), which has drawn the grid since v5.2. Nothing
   called any of them: the only surviving mention was the section comment below, and
   positionForLevel/abbreviateName/the focus handlers were reachable ONLY from inside
   the dead placeDotInDOM. ⚠ v13.1 (D04): only HALF of that was true — .is-dragging and
   .has-override really did go, but the .pi-dot* and .pi-focusing rules were still in the
   stylesheet four releases later and are removed only now. The lesson is recorded rather
   than quietly fixed: a comment asserting a deletion is a claim, not evidence. */

/* End v3.0 stakeholder block ──────────────────────────────────────── */

/* ──────────────────────────────────────────────────────────────────────
   v3.1 — Stakeholders tab: search + sort   (v5.1.1: drag-and-drop on the grid retired,
   replaced by deterministic placement + hover-focus; see placeDotInDOM / collisionOffsets)
   ────────────────────────────────────────────────────────────────────── */

/* ── SEARCH ──────────────────────────────────────────────────────────
   New stakeholder-search input. Mirrors the Actions/Minutes pattern.
   Live on every keystroke. Reuses v3.0.2 highlight machinery. */

function _stkSearchInputHandler(e) {
  stakeholderSearchQuery = (e.target.value || '').trim();
  const wrap = document.getElementById('stkSearchWrap');
  if (wrap) wrap.classList.toggle('has-text', !!e.target.value);
  renderStakeholders();
  renderMendelowGrid();  /* grid mirrors filter state */
}

function clearStakeholderSearch() {
  const inp = document.getElementById('stkSearchInput');
  if (inp) inp.value = '';
  stakeholderSearchQuery = '';
  const wrap = document.getElementById('stkSearchWrap');
  if (wrap) wrap.classList.remove('has-text');
  renderStakeholders();
  renderMendelowGrid();
  if (inp) inp.focus();
}

/* ── SORT ────────────────────────────────────────────────────────────
   Dropdown for field + asc/desc toggle button. State persists via
   appMeta.stakeholderSort (restored at boot). */

function _stkSortFieldHandler(e) {
  appMeta.stakeholderSort.field = e.target.value;
  markDirty();
  renderStakeholders();
}

function toggleStakeholderSortDir() {
  appMeta.stakeholderSort.dir = (appMeta.stakeholderSort.dir === 'asc') ? 'desc' : 'asc';
  markDirty();
  _stkSyncSortDirBtn();
  renderStakeholders();
}

function _stkSyncSortDirBtn() {
  const btn = document.getElementById('stkSortDirBtn');
  if (!btn) return;
  btn.classList.toggle('desc', appMeta.stakeholderSort.dir === 'desc');
  btn.title = appMeta.stakeholderSort.dir === 'asc'
    ? 'Sort direction: ascending — click to flip to descending'
    : 'Sort direction: descending — click to flip to ascending';
}

/* Boot-time wiring for the toolbar controls. Called after DOMContentLoaded
   along with the other initial render calls. */
function _stkBootControls() {
  const inp = document.getElementById('stkSearchInput');
  if (inp) inp.addEventListener('input', _stkSearchInputHandler);
  const sel = document.getElementById('stkSortField');
  if (sel) {
    sel.value = appMeta.stakeholderSort.field;
    sel.addEventListener('change', _stkSortFieldHandler);
  }
  _stkSyncSortDirBtn();
}

/* End v3.1 stakeholders block ──────────────────────────────────────── */

/* ── STATS + COUNTS ── */
function refreshAll() { renderStats(); updateCounts(); render(); updateLabelDatalist(); updateStakeholderDatalist(); updateOrganisationDatalist(); updateTimezoneDatalist(); syncExpandToggleLabels(); _refreshDashboardFromActions(); refreshMeetingIndicator(); if (actionsView === 'board' && typeof renderActionBoard === 'function') renderActionBoard(); }

/* ── v4.0: live "MEETING LIVE" indicator ──────────────────────────────────
   A tasteful header badge tied to the on-agenda count. Hidden at 0 agenda
   items; appears (paused, 00:00) at >0; click = start / pause / resume;
   agenda -> 0 resets + hides. Persists via appMeta.meetingTimer { elapsedMs,
   running } — we store ACCRUED elapsed + the running flag, never a wall-clock
   start, so a closed file never accrues time; reopening continues from the
   saved elapsed and resumes ticking if it was running. _meetingResumeAt /
   _meetingTick are runtime-only; _meetingReady gates refresh until boot init. */
const MT_PLAY  = '<svg viewBox="0 0 12 12" fill="currentColor"><path d="M3 2l7 4-7 4z"/></svg>';
const MT_PAUSE = '<svg viewBox="0 0 12 12" fill="currentColor"><rect x="2.5" y="2" width="2.5" height="8" rx="0.5"/><rect x="7" y="2" width="2.5" height="8" rx="0.5"/></svg>';
let _meetingResumeAt = 0;
let _meetingTick = null;
let _meetingPrevAgenda = -1;
let _meetingReady = false;

/* On-agenda count = flagged (non-deleted) action edits. Robust whether sub-actions
   are nested or top-level — each flagged entry counts once. */
function meetingAgendaCount() {
  let n = 0;
  for (const id in edits) {
    if (edits[id] && edits[id].flagged === true && !(deletedIds && deletedIds.has(+id))) n++;
  }
  return n;
}
function meetingActiveMs() {
  const t = appMeta.meetingTimer || {};
  return (t.elapsedMs || 0) + (t.running ? (Date.now() - _meetingResumeAt) : 0);
}
/* v5.1.1 §2: measured meeting duration in whole SECONDS for a session — the wrapped
   s.durationSec if present (survives the agenda-close reset), else the live timer when
   this is the current meeting. 0 when nothing was measured. */
function meetingDurationSec(s) {
  if (!s) return 0;
  if (typeof s.durationSec === 'number' && s.durationSec >= 1) return s.durationSec;
  const isCurrent = s.id === _mostRecentSessionId();   /* v14 (W4b): chronological, not array order */
  if (isCurrent) { const sec = Math.round(meetingActiveMs() / 1000); if (sec >= 1) return sec; }
  return 0;
}
/* v5.1.1 §2: human-readable duration — "47s", "3m 32s", "1h 15m 10s" (leading zero
   units omitted; seconds always shown). '' when there's nothing to show. */
function formatDuration(totalSec) {
  totalSec = Math.max(0, Math.round(totalSec || 0));
  if (totalSec < 1) return '';
  const h = Math.floor(totalSec / 3600), m = Math.floor((totalSec % 3600) / 60), s = totalSec % 60;
  const parts = [];
  if (h) parts.push(h + 'h');
  if (h || m) parts.push(m + 'm');
  parts.push(s + 's');
  return parts.join(' ');
}
function _mtFmt(ms) { const s = Math.floor(ms / 1000), h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60, p = n => String(n).padStart(2, '0'); return h > 0 ? `${p(h)}:${p(m)}:${p(ss)}` : `${p(m)}:${p(ss)}`; }
function meetingPaint() { const el = document.getElementById('liveTime'); if (el) el.textContent = _mtFmt(meetingActiveMs()); }
function _mtStartTick() { if (_meetingTick) return; _meetingTick = setInterval(meetingPaint, 250); }
function _mtStopTick() { if (_meetingTick) { clearInterval(_meetingTick); _meetingTick = null; } }
/* Fold the current running segment into accrued elapsed (called on pause + on save). */
function _mtFlush() { const t = appMeta.meetingTimer; if (t && t.running) { t.elapsedMs = (t.elapsedMs || 0) + (Date.now() - _meetingResumeAt); _meetingResumeAt = Date.now(); } }
function meetingSyncBadgeClasses() {
  const b = document.getElementById('liveBadge'); if (!b) return;
  const on = !!(appMeta.meetingTimer && appMeta.meetingTimer.running);
  b.classList.toggle('running', on);
  b.classList.toggle('paused', !on);
  const pp = document.getElementById('livePP'); if (pp) pp.innerHTML = on ? MT_PAUSE : MT_PLAY;
}
function meetingSetRunning(on) {
  if (!appMeta.meetingTimer) appMeta.meetingTimer = { elapsedMs: 0, running: false };
  if (on) {
    appMeta.meetingTimer.running = true;
    _meetingResumeAt = Date.now();
    _mtStartTick();
  } else {
    /* v5.0.2 fix: bank the running segment into elapsedMs BEFORE clearing the
       flag. _mtFlush() only folds while running===true, so clearing the flag
       first silently discarded the segment — the counter snapped back to 00:00
       on pause, and the wrapped "Actual duration" recorded 0. Flush, then clear. */
    _mtFlush();
    appMeta.meetingTimer.running = false;
    _mtStopTick();
  }
  meetingSyncBadgeClasses(); meetingPaint(); markDirty();
}
function toggleMeetingTimer() {
  if (meetingAgendaCount() === 0) return;
  meetingSetRunning(!(appMeta.meetingTimer && appMeta.meetingTimer.running));
}
/* Called once at boot, after data + DOM are ready. Restores from appMeta. */
function initMeetingIndicator() {
  if (!appMeta.meetingTimer || typeof appMeta.meetingTimer !== 'object') appMeta.meetingTimer = { elapsedMs: 0, running: false };
  _meetingReady = true;
  const wrap = document.getElementById('liveWrap'); if (!wrap) return;
  const count = meetingAgendaCount(); _meetingPrevAgenda = count;
  if (count > 0) {
    wrap.classList.remove('hidden', 'leaving', 'enter');
    if (appMeta.meetingTimer.running) { _meetingResumeAt = Date.now(); _mtStartTick(); }
    meetingSyncBadgeClasses(); meetingPaint();
  } else {
    /* ⚠ v15.2: BANK BEFORE ZEROING — the in-session wrap path snapshots the measured time
       onto the meeting (durationSec) before it resets, and this boot branch did not: save
       mid-meeting with the timer running, have the last agenda flags cleared by a
       colleague's part before your next boot, and the measured meeting time was silently
       discarded instead of landing on the meeting it measured. */
    const _bootMs = appMeta.meetingTimer.elapsedMs || 0;
    if (_bootMs >= 1000 && typeof _mostRecentSession === 'function') {
      const _cur = _mostRecentSession();
      if (_cur && !(typeof _cur.durationSec === 'number' && _cur.durationSec >= 1))
        _cur.durationSec = Math.round(_bootMs / 1000);
    }
    appMeta.meetingTimer.elapsedMs = 0; appMeta.meetingTimer.running = false;
    wrap.classList.add('hidden'); _mtStopTick(); meetingSyncBadgeClasses(); meetingPaint();
  }
}
/* Called from refreshAll() after any action change. Handles agenda transitions. */
/* v10.3 (W3): adopt the INCOMING project's stored timer on a project switch.
   refreshMeetingIndicator() is a transition detector: it compares the agenda count now
   against _meetingPrevAgenda and, when the count "appears", RESETS elapsedMs to zero.
   That is right within one project, but across a switch it made a mess — the naive fix of
   forcing prev = -1 fires the appear branch and wipes the incoming project's accrued time,
   while leaving prev alone made the timer show/bank the wrong project's minutes. So on a
   switch we do neither: adopt the stored state, resync the visuals, run no transition. */
function _mtAdoptProject() {
  if (!_meetingReady) { _meetingPrevAgenda = -1; return; }
  if (!appMeta.meetingTimer || typeof appMeta.meetingTimer !== 'object') appMeta.meetingTimer = { elapsedMs: 0, running: false };
  const count = meetingAgendaCount();
  _meetingPrevAgenda = count;                     /* no transition on the next refresh */
  _meetingResumeAt = appMeta.meetingTimer.running ? Date.now() : 0;
  const wrap = document.getElementById('liveWrap');
  if (wrap) {
    wrap.classList.remove('leaving', 'enter');
    wrap.classList.toggle('hidden', count === 0);
  }
  meetingSyncBadgeClasses();
  meetingPaint();
  if (appMeta.meetingTimer.running && count > 0) _mtStartTick(); else _mtStopTick();
}
function refreshMeetingIndicator() {
  if (!_meetingReady) return;
  const wrap = document.getElementById('liveWrap'); if (!wrap) return;
  if (!appMeta.meetingTimer) appMeta.meetingTimer = { elapsedMs: 0, running: false };
  const count = meetingAgendaCount();
  const prev = _meetingPrevAgenda; _meetingPrevAgenda = count;
  if (prev > 0 && count > 0) return;                 // still live — leave the timer alone
  if (count > 0 && prev <= 0) {                       // appear — fresh, paused at 00:00
    appMeta.meetingTimer.elapsedMs = 0; appMeta.meetingTimer.running = false; _meetingResumeAt = 0; _mtStopTick();
    wrap.classList.remove('hidden', 'leaving');
    if (prev === 0) { wrap.classList.add('enter'); setTimeout(() => wrap.classList.remove('enter'), 520); }
    meetingSyncBadgeClasses(); meetingPaint(); markDirty();
  } else if (count === 0 && prev > 0) {               // wrap — snapshot, then reset + disappear
    /* v4.0/v5.1.1 §2: snapshot the active SECONDS (pauses excluded) onto the current
       meeting (SESSIONS[0]) BEFORE the reset, so the action summary / Copy post can
       report the exact duration after the timer is gone. Any started meeting (>= 1s)
       is captured — no rounding to whole minutes. */
    const _wrapMs = meetingActiveMs();
    /* v14 (W4b): the CURRENT meeting chronologically — SESSIONS[0] is the oldest after a merge */
    const _curSession = _mostRecentSession();
    if (_curSession && _wrapMs >= 1000) _curSession.durationSec = Math.round(_wrapMs / 1000);
    _mtStopTick(); appMeta.meetingTimer.running = false; appMeta.meetingTimer.elapsedMs = 0; _meetingResumeAt = 0;
    wrap.classList.add('leaving');
    setTimeout(() => { wrap.classList.add('hidden'); wrap.classList.remove('leaving'); meetingSyncBadgeClasses(); meetingPaint(); }, 430);
    markDirty();
  }
}

/* v3.2: re-render the dashboard sections that read from ACTIONS, so they stay
   in sync as actions are added / edited / deleted. Guarded so it's a no-op if
   the functions aren't defined yet (e.g. very early boot). Deleting an action
   auto-unlinks it from any Success Project (pruneProjectActionLinks runs inside
   renderWorkstreams). */
function _refreshDashboardFromActions() {
  try {
    if (typeof renderMilestones === 'function') renderMilestones();
    if (typeof renderWorkstreams === 'function') renderWorkstreams();
    if (typeof renderKeyRisks === 'function') renderKeyRisks();
  } catch (e) {
    /* v13.1 (A29): still never let a dashboard render break the main action refresh — but
       do not swallow it in silence. Two of these three renderers REPAIR data as they go, so
       a throw here can mean a repair was abandoned half-done, and the only symptom was a
       dashboard that quietly stopped updating. Reported, not rethrown. */
    console.error('Dashboard refresh failed (the action list is unaffected):', e);
  }
}
/* v2.21: minimal toast subsystem.
   showToast(msg, opts?) — displays a transient notice at the bottom-center.
   opts.duration (default 3500ms), opts.type ('info'|'warn').
   Multiple toasts stack vertically. v2.22 will extend this with undo actions. */
function showToast(msg, opts) {
  opts = opts || {};
  const duration = opts.duration || 3500;
  const type = opts.type || 'info';
  let host = document.getElementById('toastHost');
  if (!host) {
    host = document.createElement('div');
    host.id = 'toastHost';
    document.body.appendChild(host);
  }
  const el = document.createElement('div');
  el.className = 'toast toast-' + type;
  el.textContent = msg;
  host.appendChild(el);
  // Force reflow then add visible class for animation
  void el.offsetWidth;
  el.classList.add('toast-visible');
  setTimeout(() => {
    el.classList.remove('toast-visible');
    setTimeout(() => el.remove(), 250);
  }, duration);
}

/* v2.22: pending-undo state for action and session deletes.
   Holds the snapshot needed to restore the deleted item, plus the
   timeout id so we can clear/extend it. One pending undo at a time —
   triggering a new delete dismisses the previous toast. */
let pendingUndo = null;  // { type, restore, toastEl, timeoutId } | null

function clearPendingUndo() {
  if (!pendingUndo) return;
  if (pendingUndo.timeoutId) clearTimeout(pendingUndo.timeoutId);
  if (pendingUndo.toastEl && pendingUndo.toastEl.parentNode) {
    pendingUndo.toastEl.classList.remove('toast-visible');
    const el = pendingUndo.toastEl;
    setTimeout(() => el.remove(), 250);
  }
  pendingUndo = null;
}

/* v2.22: show an undo toast bottom-LEFT (Material snackbar pattern).
   restoreFn is called if user clicks Undo within 6 seconds. */
/* ⚠ v15: `scopeOverride` — see the block above pendingUndo below. A caller that deletes a
   PROGRAMME-or-higher record passes 'portfolio'; everything else keeps the derivation. */
function showUndoToast(msg, restoreFn, scopeOverride) {
  /* ── ⚠⚠ v16.8 (W3): DISPLACING A PENDING UNDO IS NO LONGER SILENT ────────────────
     Delete something; before the toast fades, delete something else; press Undo. The SECOND
     item comes back and the FIRST is gone for good. SWEEP-2 captured every message the app
     raised across the whole sequence and it was exactly this: ["First thing deleted",
     "Second thing deleted"]. Nothing mentioned that the first had just become permanent.
     ⚠⚠ THE SINGLE SHARED SLOT IS DESIGNED AND STAYS. This call is why — every one of the
     twelve delete types funnels through here, and CLAUDE.md documents the one-slot rule.
     **The defect was never the mechanism; it was the silence.** Reported under method rule 6:
     a past ruling that a mechanism is intended does not suppress a finding about what it
     costs the user.
     ⚠ P4b executed the pairing that matters — a person, then a project — where the discarded
     item is somebody's whole body of work rather than one action.
     ⚠ The message NAMES the displaced delete. "Something earlier can no longer be undone"
     would be its own small cruelty, and this code already holds the label it needs. */
  const _displaced = (pendingUndo && pendingUndo.msg) ? String(pendingUndo.msg) : null;
  /* Dismiss any prior pending undo (commits prior deletion) */
  clearPendingUndo();

  let host = document.getElementById('toastHostBottomLeft');
  if (!host) {
    host = document.createElement('div');
    host.id = 'toastHostBottomLeft';
    document.body.appendChild(host);
  }
  const el = document.createElement('div');
  el.className = 'toast toast-undo';
  const msgSpan = document.createElement('span');
  msgSpan.textContent = msg;
  el.appendChild(msgSpan);
  const btn = document.createElement('button');
  btn.className = 'toast-undo-btn';
  btn.textContent = 'Undo';
  btn.onclick = () => {
    /* v10.3 (W3, second defence): a restore closure resolves the LIVE module bindings
       (ACTIONS, edits, appMeta …) when it runs, so firing one against a different project
       writes the deleted record into THAT project — clobbering its same-numbered action.
       setActiveProject now clears any pending undo, so this should be unreachable; the
       stamp makes it structurally impossible rather than merely unlikely. */
    /* ── ⚠⚠ v16.8 (W2): NULL MEANS NO PROJECT IS OPEN — IT DOES NOT MEAN THE USER MOVED ──
       This said *"belonged to another project"* whatever `activeProjectId` held, including
       null. Null is not a different project; it is no project, and the user need not have
       navigated anywhere to reach it — adoption and selectProgramme both produce it. The
       message therefore named a switch that had not happened.
       ⚠⚠ THIS IS THE INVENTED-CAUSE CLASS v10.4.2 OUTLAWED AND ITS THIRD RECURRENCE IN THIS
       ONE GUARD: v15 fixed it for a programme cascade, v15.2 again for a portfolio-scoped
       undo, each time by correcting the CALLER that reached it. **Fixing a caller a third
       time invites a fourth.** The refusal itself is correct and stays; what changes is that
       the sentence now states only what this code can actually see. */
    if (pendingUndo && pendingUndo.projectId && pendingUndo.projectId !== activeProjectId) {
      clearPendingUndo();
      showToast(activeProjectId
                  ? 'That undo belonged to another project and has expired.'
                  : 'That undo belonged to a project that is no longer open, so it has expired.',
                { type: 'warn' });
      return;
    }
    /* ── v13.1 (A03): the same guard, one level up. A programme-level record (a benefit)
       is restored by a closure that resolves its destination LAZILY through _progBenefits()
       — i.e. into whichever programme is active WHEN UNDO IS CLICKED, not the one it was
       deleted from. selectProgramme sets activeProjectId = null, so the project stamp above
       could not catch it: programme A lost the benefit and programme B silently gained it,
       keeping A's BEN-nnn number, which can collide with one of B's. */
    /* ⚠ v16.8 (W2): the same correction, one level up — for the same reason, and stated
       here too rather than left to be inferred from the block above. */
    if (pendingUndo && pendingUndo.programmeId
        && typeof activeProgrammeId !== 'undefined'
        && pendingUndo.programmeId !== activeProgrammeId) {
      clearPendingUndo();
      showToast(activeProgrammeId
                  ? 'That undo belonged to another programme and has expired.'
                  : 'That undo belonged to a programme that is no longer open, so it has expired.',
                { type: 'warn' });
      return;
    }
    if (pendingUndo && pendingUndo.restore) pendingUndo.restore();
    clearPendingUndo();
  };
  el.appendChild(btn);
  host.appendChild(el);
  void el.offsetWidth;
  el.classList.add('toast-visible');

  const timeoutId = setTimeout(() => {
    if (pendingUndo && pendingUndo.toastEl === el) {
      clearPendingUndo();
    }
  }, 6000);

  /* v10.3 (W3): stamp the owning project — see the Undo click handler above.
     ── v13.1 (A02/A03): also stamp the SCOPE and the owning programme.
     `scope` exists because setActiveProject clears every pending undo, and that rule is
     right for a PROJECT-scoped one (its closure resolves live per-project bindings) but
     wrong for a PORTFOLIO-scoped one. A programme cascade restores by array index into
     db.programmes / db.projects and has no stale-binding exposure at all, yet opening any
     project inside the six seconds destroyed it silently and the deletion became permanent
     (reproduced in the v13 review). A portfolio-scoped undo now survives a project switch
     and expires only on its own timeout.
     `programmeId` exists because selectProgramme sets activeProjectId = null, so the
     project stamp could not guard a programme-level record: deleting a benefit in A,
     switching to B and clicking Undo put A's benefit into B (also reproduced).
     ⚠⚠ v15: "Scope is DERIVED, not passed" WAS THE BUG, and this is the correction. The
     derivation read `activeProjectId ? 'project' : 'portfolio'`, but `activeProjectId` is
     STICKY: nothing clears it when you leave a project for the Overview or the Portfolio.
     So once any project had been opened in the session, EVERY later delete anywhere was
     stamped 'project' — and setActiveProject then destroyed it, committing the delete
     silently. That is A02/A03 still live for exactly the three deletes that have no
     stale-binding exposure at all: a benefit (anchored by `_srcProgId`, spliced into
     `_P.benefits`), a NON-ACTIVE programme cascade (`wasActive` false, so activeProjectId is
     never cleared) and a project delete (`deleteProject` never clears it either).
     ⚠ It also produced a WRONG EXPLANATION: after selectProgramme nulls activeProjectId the
     Undo button's own guard reported "that undo belonged to another project and has expired"
     for a programme-level delete. A failure must not invent a reason (v10.4.2's rule).
     Scope is now DERIVED BY DEFAULT and OVERRIDDEN BY THE CALLER, because the caller is the
     only thing that actually knows what it deleted. */
  /* ⚠ v15.2: a PORTFOLIO-scoped undo carries NO project/programme stamp. v15 made it survive
     the project switch, but the Undo BUTTON's own guards still read the stamps — and
     activeProjectId is sticky — so clicking Undo after a switch refused with "that undo
     belonged to another project", the invented-reason class, and committed the delete anyway.
     A portfolio-scoped restore resolves by value against __DB__ and belongs to no project;
     stamping it with whichever project happened to be open was the lie the guards then
     believed. */
  const _scope = scopeOverride || (activeProjectId ? 'project' : 'portfolio');
  pendingUndo = { type: 'delete', restore: restoreFn, toastEl: el, timeoutId,
                  /* v16.8 (W3): the label, so the NEXT delete can name what it displaces.
                     Session-only, like every other field here — nothing is serialised. */
                  msg: String(msg == null ? '' : msg),
                  projectId: _scope === 'portfolio' ? null : activeProjectId,
                  programmeId: _scope === 'portfolio' ? null
                    : (typeof activeProgrammeId !== 'undefined' ? activeProgrammeId : null),
                  scope: _scope };
  /* ⚠ v16.8 (W3): and NOW say what the new toast just cost, once the slot is consistent.
     Raised after the assignment rather than before `clearPendingUndo()` so nothing can leave
     the user warned about a displacement that then failed to happen. */
  if (_displaced && typeof showToast === 'function') {
    showToast('“' + _displaced + '” can no longer be undone — only the most recent deletion can.',
              { type: 'warn', duration: 7000 });
  }
}

/* v2.21: action-card navigation helper. Used by clickable chips.
   - Scrolls target into view (block:'nearest', same as v2.19 item 7).
   - Expands the card if collapsed.
   - Brief amber flash (~700ms) for visual locator.
   - If target is hidden by current filter, shows toast and aborts.
   targetId: number; visibleIds: Set<number> of currently rendered actions. */
function navigateToAction(targetId) {
  const card = document.getElementById('card-' + targetId);
  if (!card) {
    showToast('Action ' + displayActionId(targetId) + ' is hidden by the current filter', { type: 'warn' });   /* v15.2: derived number, never the raw block id */
    return;
  }
  card.classList.add('expanded');
  card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  card.classList.add('chip-flash');
  setTimeout(() => card.classList.remove('chip-flash'), 700);
}

/* v2.21: cycle detection for dependsOn.
   Given a graph defined by edits[id].dependsOn, return true if adding
   target as a blocker of source would create a cycle. Walks the graph
   starting from target — if it reaches source, cycle. Self-references
   count as a cycle too (and are also stripped at the call site). */
function wouldCreateDependencyCycle(sourceId, targetId) {
  if (sourceId === targetId) return true;
  /* Walk: from target, follow its dependsOn chain. If source appears,
     adding target as source's blocker would close the loop. */
  const visited = new Set();
  const stack = [targetId];
  while (stack.length) {
    const cur = stack.pop();
    if (visited.has(cur)) continue;
    visited.add(cur);
    if (cur === sourceId) return true;
    const e = edits[cur];
    if (e && Array.isArray(e.dependsOn)) {
      for (const x of e.dependsOn) if (!visited.has(x)) stack.push(x);
    }
  }
  return false;
}

/* v2.21: add/remove dependency or related link.
   For dependsOn: cycle-check before adding; reject with toast on cycle.
   For relatedTo: no cycle check. */
function addDependency(sourceId, targetId) {
  if (!Number.isFinite(targetId) || targetId === sourceId) {
    showToast('Cannot depend on itself', { type: 'warn' });
    return;
  }
  const validIds = new Set([...ACTIONS.filter(a => !deletedIds.has(a.id)), ...newActions].map(a => a.id));
  if (!validIds.has(targetId)) {
    showToast('Action #' + targetId + ' does not exist', { type: 'warn' });
    return;
  }
  const e = edits[sourceId];
  if (e.dependsOn.includes(targetId)) return;  // no-op, already there
  if (wouldCreateDependencyCycle(sourceId, targetId)) {
    showToast('Cannot add #' + targetId + ' — would create a dependency cycle', { type: 'warn' });
    return;
  }
  e.dependsOn.push(targetId);
  sessionEditedActionIds.add(sourceId);
  markDirty();
  refreshAll();
}
function removeDependency(sourceId, targetId) {
  const e = edits[sourceId];
  if (!e || !Array.isArray(e.dependsOn)) return;
  const i = e.dependsOn.indexOf(targetId);
  if (i !== -1) e.dependsOn.splice(i, 1);
  sessionEditedActionIds.add(sourceId);
  markDirty();
  refreshAll();
}
function addRelated(sourceId, targetId) {
  if (!Number.isFinite(targetId) || targetId === sourceId) {
    showToast('Cannot relate to itself', { type: 'warn' });
    return;
  }
  const validIds = new Set([...ACTIONS.filter(a => !deletedIds.has(a.id)), ...newActions].map(a => a.id));
  if (!validIds.has(targetId)) {
    showToast('Action #' + targetId + ' does not exist', { type: 'warn' });
    return;
  }
  const e = edits[sourceId];
  if (e.relatedTo.includes(targetId)) return;
  e.relatedTo.push(targetId);
  sessionEditedActionIds.add(sourceId);
  markDirty();
  refreshAll();
}
function removeRelated(sourceId, targetId) {
  const e = edits[sourceId];
  if (!e || !Array.isArray(e.relatedTo)) return;
  const i = e.relatedTo.indexOf(targetId);
  if (i !== -1) e.relatedTo.splice(i, 1);
  sessionEditedActionIds.add(sourceId);
  markDirty();
  refreshAll();
}

/* v2.21: input handler for the inline chip-add input fields.
   Parses '#5' / '5' / '#5: anything else' — the leading number after an optional '#'.
   ⚠ v14.2 (b): what is typed is now a SHORT DISPLAY NUMBER, so it is RESOLVED to the real
   action id before anything is stored. Display and input had to move together or typing what
   you see would fail. '#4.2' now works too - a sub-action can be a blocker. */
function parseDependencyInput(value) {
  return actionIdFromDisplay(value);
}
function handleDependencyInput(sourceId, fieldKind, input) {
  /* fieldKind: 'dependsOn' | 'relatedTo' */
  const targetId = parseDependencyInput(input.value);
  if (targetId == null) {
    /* v14.2 (b): "number", because that is now what is on the card and what resolves. */
    showToast('Enter an action number you can see on a card (e.g. #5)', { type: 'warn' });
    return;
  }
  if (fieldKind === 'dependsOn') addDependency(sourceId, targetId);
  else addRelated(sourceId, targetId);
  input.value = '';
}

/* v2.21: returns true if a given action has at least one open blocker.
   Open = Not yet started / In progress / On hold. Used by render for the
   collapsed-card visual marker. */
/* ── ⚠ v13.1 (A52): ONE definition of "this blocker still blocks" ───────────────────────
   "Blocked" was computed in two places that disagreed. The Kanban card excluded blockers in
   `deletedIds`; this one did not — so an action whose only blocker you had DELETED this
   session still wore the amber BLOCKED tag on its card while the board had already let it
   go. Both now call this, so the question has a single answer.
   ⚠ deletedIds membership is the reason a shared helper is needed at all: a delete is not
   applied to the arrays until Save, so "deleted" is state only this function should know
   how to read. */
function _blockerStillBlocks(blockerId) {
  const be = edits[blockerId];
  if (!be) return false;                        /* orphans handled at load — defensive */
  if (deletedIds && deletedIds.has(blockerId)) return false;   /* deleted this session */
  return !isClosedStatus(be.status);            /* open status blocks */
}
function hasOpenBlocker(actionId) {
  const e = edits[actionId];
  if (!e || !Array.isArray(e.dependsOn) || e.dependsOn.length === 0) return false;
  return e.dependsOn.some(_blockerStillBlocks);
}

/* v2.21: returns derived chips: actions that depend on / are related to this one.
   Read-only on the target card (edit happens on the source). */
function getDerivedBlockedBy(actionId) {
  /* Find all actions whose dependsOn includes this actionId */
  const result = [];
  Object.keys(edits).forEach(idStr => {
    const id = Number(idStr);
    const e = edits[id];
    if (e && Array.isArray(e.dependsOn) && e.dependsOn.includes(actionId)) {
      result.push(id);
    }
  });
  return result;
}
function getDerivedRelatedFrom(actionId) {
  const result = [];
  Object.keys(edits).forEach(idStr => {
    const id = Number(idStr);
    const e = edits[id];
    if (e && Array.isArray(e.relatedTo) && e.relatedTo.includes(actionId)) {
      result.push(id);
    }
  });
  return result;
}

/* v2.21: shortened description for chip text.
   '#5: <desc, truncated>' for nicer chip readability.
   v2.91: uses displayActionId() so sub-action chips show #18.1 notation
   (matches what's on the card). Underlying integer id is still used for
   storage and lookup; only the display string changes. */
function chipLabel(id) {
  const e = edits[id];
  if (!e) return '#?';   /* v14.2 (b): a dangling chip says so, rather than printing a block id */
  const desc = (e.desc || '').replace(/\s+/g, ' ').trim();
  const truncated = desc.length > 60 ? desc.slice(0, 57) + '…' : desc;
  return displayActionId(id) + (truncated ? ': ' + truncated : '');
}

/* v2.90 ── SUB-ACTION HELPERS ──
   Single-level nesting: an action either has parentId=null (top-level) or
   parentId=<some top-level action id>. No grand-children allowed. */

/* True if this action has at least one sub-action under it. */
function hasSubActions(parentId) {
  if (typeof parentId !== 'number') return false;
  return getAllActions().some(a => {
    const e = edits[a.id];
    return e && e.parentId === parentId;
  });
}

/* Return array of action IDs whose parentId === this id. Stable order: by ID ascending. */
function getSubActionIds(parentId) {
  const ids = [];
  getAllActions().forEach(a => {
    const e = edits[a.id];
    if (e && e.parentId === parentId) ids.push(a.id);
  });
  return ids.sort((a, b) => a - b);
}

/* Return rollup progress for a parent's sub-actions.
   Returns { done: <int>, total: <int>, pct: <0-100> } where:
     - done = sub-actions with status Completed or Cancelled
     - total = all sub-actions
     - pct = done/total * 100 (0 if total === 0) */
/* v10.3 (W5): getSubActionRollup removed — nothing called it; the sub-action progress
   shown on cards comes from _renderSubsContainerHTML. ⚠ v13.1 (U03): that CSS did NOT go
   with it — three .subs-rollup rules outlived the comment by four releases and were
   finally removed here. A comment is not a census. */
/* Cycle prevention for parent assignment.
   Single-level nesting means: candidateParentId itself must have parentId === null.
   Returns true if it's safe to set `actionId`'s parent to `candidateParentId`. */
function canSetParent(actionId, candidateParentId) {
  if (typeof candidateParentId !== 'number') return true;  /* null is always ok */
  if (candidateParentId === actionId) return false;       /* self-parent forbidden */
  const candEdit = edits[candidateParentId];
  if (!candEdit) return false;                            /* candidate doesn't exist */
  /* Single-level rule: candidate parent must itself be top-level */
  if (candEdit.parentId != null) return false;
  /* Also: actionId must not currently have any sub-actions (would create grand-children) */
  if (hasSubActions(actionId)) return false;
  return true;
}

